import React, { useMemo, useState } from "react";
import IPendingItemsOnReleaseItem from "@models/DTO/IPendingItemsOnReleaseItem";
import PendingItemsActionEnum from "@models/PendingItemsActionEnum";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";
import UIService from "@services/UIService";
import "./PendingItemsOnReleaseModal.scss";

type Props = {
    items: IPendingItemsOnReleaseItem[];
    message?: string;
    cancelFn: () => void;
    onAction: (action: PendingItemsActionEnum) => void;
};

export const PendingItemsOnReleaseModal = (props: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showOptions, setShowOptions] = useState(false);

    const handleAction = (action: PendingItemsActionEnum) => {
        setIsSubmitting(true);
        props.onAction(action);
    };

    const noneAllowBackorder = useMemo(() => props.items.every((i) => !i.allowBackorder), [props.items]);
    const mixedBackorder = useMemo(
        () => props.items.some((i) => i.allowBackorder) && props.items.some((i) => !i.allowBackorder),
        [props.items]
    );
    const nonBackorderableSkus = useMemo(
        () =>
            props.items
                .filter((i) => !i.allowBackorder)
                .map((i) => i.catalogItemSku)
                .join(", "),
        [props.items]
    );
    const showBackorderButton = !noneAllowBackorder;

    return (
        <div className="PendingItemsOnReleaseModal">
            <div className="modal-header">
                <OwTitle title="pending_backorder_items" tag="h5" />
            </div>
            <div className="modal-body">
                {props.message ? <p className="text-danger mb-2">{props.message}</p> : null}
                <p className="mb-3">
                    <OwTranslate
                        id="pending_items_on_release_description"
                        defaultMessage="Available inventory will be automatically allocated, but the remaining quantities need your attention."
                    />
                </p>
                <table className={"table table-sm " + (UIService.isMainViewLight() ? "" : "table-dark")}>
                    <thead>
                        <tr>
                            <th>
                                <OwLabel title="item" noBottomMargin={true} />
                            </th>
                            <th>
                                <OwLabel title="sku" noBottomMargin={true} />
                            </th>
                            <th>
                                <OwLabel title="order_qty" noBottomMargin={true} />
                            </th>
                            <th>
                                <OwLabel title="pending" noBottomMargin={true} />
                            </th>
                            <th>
                                <OwLabel title="can_fulfill" noBottomMargin={true} />
                            </th>
                            <th>
                                <OwLabel title="still_pending" noBottomMargin={true} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.items.map((item) => (
                            <tr key={item.orderItemId}>
                                <td>
                                    {item.catalogItemName}
                                    {!item.allowBackorder && (
                                        <span className="text-muted fst-italic ms-2">
                                            (<OwTranslate id="no_backorder" defaultMessage="No Backorder" />)
                                        </span>
                                    )}
                                </td>
                                <td>{item.catalogItemSku}</td>
                                <td>{item.qty}</td>
                                <td>{item.qtyPending}</td>
                                <td>{item.qtyFulfillable}</td>
                                <td className={item.qtyStillPending > 0 ? "text-warning fw-bold" : ""}>{item.qtyStillPending}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {mixedBackorder && (
                    <div className="alert alert-info mt-2 mb-0">
                        <OwTranslate
                            id="backorder_mixed_info"
                            defaultMessage="Only items that allow backorders will be included in the backorder. Items that don't allow backorders ({skus}) will be removed from the order."
                            values={{ skus: nonBackorderableSkus }}
                        />
                    </div>
                )}
                <div className="mt-3">
                    <a
                        className="text-decoration-none pointer d-inline-flex align-items-center"
                        onClick={() => setShowOptions(!showOptions)}>
                        <i className={`me-1 fas fa-chevron-${showOptions ? "down" : "right"} fa-xs`} />
                        <OwTranslate id="understand_your_options" defaultMessage="Understand your options" />
                    </a>
                    {showOptions && (
                        <ul className="mt-2 mb-3 pending-items-options-list">
                            {showBackorderButton && (
                                <li>
                                    <strong>
                                        <OwTranslate id="backorder" defaultMessage="Backorder" />
                                    </strong>{" "}
                                    &mdash;{" "}
                                    <OwTranslate
                                        id="pending_items_backorder_description"
                                        defaultMessage="Ship what's available now and move the remaining unfulfilled quantity to a new backorder. The backorder will be filled when inventory becomes available. (Only available for items that allow backorders.)"
                                    />
                                </li>
                            )}
                            <li>
                                <strong>
                                    <OwTranslate id="remove" defaultMessage="Remove" />
                                </strong>{" "}
                                &mdash;{" "}
                                <OwTranslate
                                    id="pending_items_remove_description"
                                    defaultMessage="Ship what's available now and reduce the order quantity to match. The unfulfilled quantity is removed from the order entirely — as if it was never ordered."
                                />
                            </li>
                            <li>
                                <strong>
                                    <OwTranslate id="zero_out" defaultMessage="Zero Out" />
                                </strong>{" "}
                                &mdash;{" "}
                                <OwTranslate
                                    id="pending_items_zero_out_description"
                                    defaultMessage="Ship what's available now and keep the original order quantity unchanged, but mark the unfulfilled quantity as zero-shipped. A note is added to the order item recording the removed quantity. This preserves the order history showing what was originally requested vs. what was actually shipped."
                                />
                            </li>
                        </ul>
                    )}
                </div>
            </div>
            <div className="modal-footer">
                <OwButton title="cancel" fn={props.cancelFn} className="btn-secondary" disabled={isSubmitting} />
                {showBackorderButton && (
                    <OwButton
                        title="create_backorder"
                        fn={() => handleAction(PendingItemsActionEnum.BACKORDER)}
                        className="btn-primary"
                        disabled={isSubmitting}
                    />
                )}
                <OwButton
                    title="remove_items"
                    fn={() => handleAction(PendingItemsActionEnum.REMOVE)}
                    className="btn-warning"
                    disabled={isSubmitting}
                />
                <OwButton
                    title="zero_out_and_add_note"
                    fn={() => handleAction(PendingItemsActionEnum.ZERO_OUT)}
                    className="btn-danger"
                    disabled={isSubmitting}
                />
            </div>
        </div>
    );
};
